import React, { useEffect, useState } from "react";
import { Box, Button, Chip, CircularProgress, Paper, Typography } from "@mui/material";
import DropDownValue from "../../baseComponents/dropdownvalue";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { TcnTemplate, TemplateUserMapping } from "../../../interfaces/tcnTemplate";
import {
  fetchTcnTemplatesApi,
  fetchTemplateUsersApi,
  updateMappingApi,
  deleteMappingApi,
} from "../../../services/manageTemplatesService";
import useIsMounted from "../../../utils/useMountedRef";

const ManageDialerTemplate: React.FC = () => {
  const [tcnTemplates, setTcnTemplates] = useState<TcnTemplate[]>([]);
  const [selectedTcnTemplate, setSelectedTcnTemplate] = useState<string>("");
  const [templateUsers, setTemplateUsers] = useState<TemplateUserMapping[]>([]);
  const allUsers = useSelector((state: RootState) => state.userList.users).map(
    (val) => val.username
  );
  const [isLoadingMappings, setIsLoadingMappings] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [editingAgentId, setEditingAgentId] = useState<string | null>(null);
  const [selectedNewUser, setSelectedNewUser] = useState<string>("");
  const isMountedRef = useIsMounted();

  const fetchTcnTemplates = async () => {
    try {
      const data = await fetchTcnTemplatesApi();
      if (!isMountedRef.current) return;
      setTcnTemplates(data);
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error(error);
      setTcnTemplates([]);
    }
  };

  const fetchTemplateUsers = async (templateName: string) => {
    if (!templateName) {
      if (!isMountedRef.current) return;
      setTemplateUsers([]);
      return;
    }
    setIsLoadingMappings(true);
    try {
      const users = await fetchTemplateUsersApi(templateName);
      if (!isMountedRef.current) return;
      setTemplateUsers(users);
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error(error);
      setTemplateUsers([]);
    } finally {
      if (isMountedRef.current) {
        setIsLoadingMappings(false);
      }
    }
  };

  const getAvailableUsers = (currentUsername: string) => {
    return allUsers.filter((username) => username !== currentUsername);
  };

  const handleUpdateMapping = async (mapping: TemplateUserMapping, newUsername: string) => {
    if (!newUsername || !selectedTcnTemplate) return;
    setIsSaving(true);
    try {
      const response = await updateMappingApi(mapping, newUsername);
      if (response.status === 200 || response.status === 201) {
        alert(`Mapping updated successfully`);
        if (!isMountedRef.current) return;
        setEditingAgentId(null);
        setSelectedNewUser("");
        fetchTemplateUsers(selectedTcnTemplate);
      }
    } catch (error: any) {
      console.error(error);
      alert(error?.response?.data?.message || "Failed to update agent mapping");
    } finally {
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingAgentId(null);
    setSelectedNewUser("");
  };

  const handleDeleteMapping = async (mapping: TemplateUserMapping) => {
    if (!mapping.mapping_id) {
      alert("Cannot delete: mapping ID not found");
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete the mapping for ${mapping.ce_username || "this agent"}?`
    );
    if (!confirmed) return;

    setIsDeleting(mapping.mapping_id);
    try {
      const response = await deleteMappingApi(mapping.mapping_id);
      if (response.status === 200 || response.status === 204) {
        alert("Mapping deleted successfully");
        if (!isMountedRef.current) return;
        fetchTemplateUsers(selectedTcnTemplate);
      }
    } catch (error: any) {
      console.error(error);
      alert(error?.response?.data?.message || "Failed to delete mapping");
    } finally {
      if (isMountedRef.current) {
        setIsDeleting(null);
      }
    }
  };

  const handleTemplateChange = (value: string) => {
    setSelectedTcnTemplate(value);
    setEditingAgentId(null);
    setSelectedNewUser("");
    fetchTemplateUsers(value);
  };

  useEffect(() => {
    fetchTcnTemplates();
  }, []);

  return (
    <Box sx={{ ml: 2, mr: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" px={2} py={1} mb={2}>
        <Typography variant="h6" component="h2">
          Manage Agent Mappings
        </Typography>
      </Box>

      <Paper sx={{ p: 2 }} elevation={2}>
        <Box sx={{ mb: 3 }}>
          <DropDownValue
            label="Select TCN Template"
            selectedValue={selectedTcnTemplate}
            onSelection={(v) => handleTemplateChange(v as string)}
            items={tcnTemplates}
            valueProp="template_name"
            displayProp="template_name"
            keyProp="id"
            multiple={false}
            noOptionsText="No templates available"
          />
        </Box>

        {selectedTcnTemplate && (
          <>
            <Typography variant="subtitle1" gutterBottom>
              TCN Agent to User Mappings
            </Typography>

            {isLoadingMappings ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={32} />
              </Box>
            ) : templateUsers.length === 0 ? (
              <Typography variant="body2" color="text.secondary" py={2}>
                No agent mappings found for this template
              </Typography>
            ) : (
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", mt: 2 }}>
                <Box component="thead">
                  <Box
                    component="tr"
                    sx={{ borderBottom: "1px solid", borderColor: "divider" }}
                  >
                    <Box
                      component="th"
                      sx={{ textAlign: "left", py: 1.5, px: 2, fontWeight: 600 }}
                    >
                      <Typography variant="subtitle2">TCN Agent</Typography>
                    </Box>
                    <Box
                      component="th"
                      sx={{ textAlign: "left", py: 1.5, px: 2, fontWeight: 600 }}
                    >
                      <Typography variant="subtitle2">User</Typography>
                    </Box>
                    <Box
                      component="th"
                      sx={{ textAlign: "right", py: 1.5, px: 2, fontWeight: 600 }}
                    >
                      <Typography variant="subtitle2">Action</Typography>
                    </Box>
                  </Box>
                </Box>
                <Box component="tbody">
                  {templateUsers.map((mapping, index) => (
                    <Box
                      component="tr"
                      key={mapping.tcn_agent_id}
                      sx={{
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <Box component="td" sx={{ py: 1.5, px: 2 }}>
                        <Chip
                          label={`Agent ${index + 1}`}
                          color="secondary"
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      <Box component="td" sx={{ py: 1.5, px: 2 }}>
                        {mapping.ce_username ? (
                          <Chip
                            label={mapping.ce_username}
                            color="primary"
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No user assigned
                          </Typography>
                        )}
                      </Box>
                      <Box component="td" sx={{ py: 1.5, px: 2, textAlign: "right" }}>
                        {editingAgentId === mapping.tcn_agent_id ? (
                          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                            <Box sx={{ minWidth: 180 }}>
                              <DropDownValue
                                label="Select User"
                                selectedValue={selectedNewUser}
                                onSelection={(v) => setSelectedNewUser(v as string)}
                                items={getAvailableUsers(mapping.ce_username)}
                                multiple={false}
                                noOptionsText="No other users"
                              />
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleUpdateMapping(mapping, selectedNewUser)}
                              disabled={!selectedNewUser || isSaving}
                            >
                              {isSaving ? <CircularProgress size={16} /> : "Save"}
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={handleCancelEdit}
                              disabled={isSaving}
                            >
                              Cancel
                            </Button>
                          </Box>
                        ) : (
                          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                setEditingAgentId(mapping.tcn_agent_id);
                                setSelectedNewUser("");
                              }}
                            >
                              Change User
                            </Button>
                            {mapping.mapping_id && (
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() => handleDeleteMapping(mapping)}
                                disabled={isDeleting === mapping.mapping_id}
                              >
                                {isDeleting === mapping.mapping_id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  "Delete"
                                )}
                              </Button>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ManageDialerTemplate;


